import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { FaUser, FaBuilding, FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';

import { clientActions } from '../../sagas/clientSlice';

const validationSchema = Yup.object({
  nom: Yup.string().when('typeClient', {
    is: 'PARTICULIER',
    then: Yup.string().required('Nom requis')
  }),
  prenom: Yup.string().when('typeClient', {
    is: 'PARTICULIER',
    then: Yup.string().required('Prénom requis')
  }),
  entreprise: Yup.string().when('typeClient', {
    is: 'ENTREPRISE',
    then: Yup.string().required('Nom de l\'entreprise requis')
  }),
  ice: Yup.string().when('typeClient', {
    is: 'ENTREPRISE',
    then: Yup.string().required('Numéro ICE requis')
  }),
  telephone1: Yup.string().required('Téléphone requis'),
  adresse: Yup.string().required('Adresse requise'),
  email: Yup.string().email('Email invalide')
});

const ClientForm = ({ onClientAdded, onCancel }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const formik = useFormik({
    initialValues: {
      typeClient: 'PARTICULIER',
      nom: '',
      prenom: '',
      entreprise: '',
      ice: '',
      cin: '',
      telephone1: '',
      telephone2: '',
      email: '',
      adresse: '',
      ville: 'Casablanca',
      codePostal: '',
      active: true
    },
    validationSchema,
    onSubmit: (values) => {
      dispatch(clientActions.addClient({
        data: values,
        onSuccess: (newClient) => {
          Swal.fire({
            icon: 'success',
            title: t('client.success'),
            text: t('client.addedSuccessfully'),
            timer: 2000,
            showConfirmButton: false
          });
          onClientAdded(newClient);
        },
        onError: (error) => {
          Swal.fire("Erreur", error, "error");
        }
      }));
    }
  });

  const villesMaroc = [
    'Casablanca', 'Rabat', 'Marrakech', 'Fès', 'Tanger',
    'Agadir', 'Meknès', 'Oujda', 'Kénitra', 'Tétouan',
    'Safi', 'Mohammedia', 'El Jadida', 'Nador', 'Beni Mellal'
  ];

  return (
    <Card>
      <CardBody>
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={12}>
              <FormGroup>
                <Label for="typeClient">
                  {formik.values.typeClient === 'PARTICULIER' ? <FaUser className="me-1" /> : <FaBuilding className="me-1" />}
                  {t('client.type')} *
                </Label>
                <Input
                  type="select"
                  id="typeClient"
                  name="typeClient"
                  value={formik.values.typeClient}
                  onChange={formik.handleChange}
                >
                  <option value="PARTICULIER">{t('client.particulier')}</option>
                  <option value="ENTREPRISE">{t('client.entreprise')}</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>

          {formik.values.typeClient === 'PARTICULIER' ? (
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="nom">{t('client.nom')} *</Label>
                  <Input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formik.values.nom}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.nom && formik.errors.nom}
                    placeholder={t('client.nomPlaceholder')}
                  />
                  {formik.touched.nom && formik.errors.nom && (
                    <div className="invalid-feedback">{formik.errors.nom}</div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="prenom">{t('client.prenom')} *</Label>
                  <Input
                    type="text"
                    id="prenom"
                    name="prenom"
                    value={formik.values.prenom}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.prenom && formik.errors.prenom}
                    placeholder={t('client.prenomPlaceholder')}
                  />
                  {formik.touched.prenom && formik.errors.prenom && (
                    <div className="invalid-feedback">{formik.errors.prenom}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="entreprise">{t('client.entreprise')} *</Label>
                  <Input
                    type="text"
                    id="entreprise"
                    name="entreprise"
                    value={formik.values.entreprise}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.entreprise && formik.errors.entreprise}
                    placeholder={t('client.entreprisePlaceholder')}
                  />
                  {formik.touched.entreprise && formik.errors.entreprise && (
                    <div className="invalid-feedback">{formik.errors.entreprise}</div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="ice">{t('client.ice')} *</Label>
                  <Input
                    type="text"
                    id="ice"
                    name="ice"
                    value={formik.values.ice}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.ice && formik.errors.ice}
                    placeholder={t('client.icePlaceholder')}
                  />
                  {formik.touched.ice && formik.errors.ice && (
                    <div className="invalid-feedback">{formik.errors.ice}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          )}

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="telephone1">
                  <FaPhone className="me-1" />
                  {t('client.telephone1')} *
                </Label>
                <Input
                  type="tel"
                  id="telephone1"
                  name="telephone1"
                  value={formik.values.telephone1}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.telephone1 && formik.errors.telephone1}
                  placeholder="06 12 34 56 78"
                />
                {formik.touched.telephone1 && formik.errors.telephone1 && (
                  <div className="invalid-feedback">{formik.errors.telephone1}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="telephone2">
                  <FaPhone className="me-1" />
                  {t('client.telephone2')}
                </Label>
                <Input
                  type="tel"
                  id="telephone2"
                  name="telephone2"
                  value={formik.values.telephone2}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="05 22 34 56 78"
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="email">
                  <FaEnvelope className="me-1" />
                  {t('client.email')}
                </Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.email && formik.errors.email}
                  placeholder="client@example.com"
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="invalid-feedback">{formik.errors.email}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="cin">{t('client.cin')}</Label>
                <Input
                  type="text"
                  id="cin"
                  name="cin"
                  value={formik.values.cin}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="AB123456"
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={8}>
              <FormGroup>
                <Label for="adresse">
                  <FaMapMarkerAlt className="me-1" />
                  {t('client.adresse')} *
                </Label>
                <Input
                  type="textarea"
                  id="adresse"
                  name="adresse"
                  rows="2"
                  value={formik.values.adresse}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.adresse && formik.errors.adresse}
                  placeholder={t('client.adressePlaceholder')}
                />
                {formik.touched.adresse && formik.errors.adresse && (
                  <div className="invalid-feedback">{formik.errors.adresse}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="codePostal">{t('client.codePostal')}</Label>
                <Input
                  type="text"
                  id="codePostal"
                  name="codePostal"
                  value={formik.values.codePostal}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="20000"
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="ville">
                  <FaMapMarkerAlt className="me-1" />
                  {t('client.ville')}
                </Label>
                <Input
                  type="select"
                  id="ville"
                  name="ville"
                  value={formik.values.ville}
                  onChange={formik.handleChange}
                >
                  {villesMaroc.map(ville => (
                    <option key={ville} value={ville}>{ville}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button
              type="button"
              color="secondary"
              onClick={onCancel}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  );
};

export default ClientForm;
